<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('ledger_recon_title') }}
          </h3>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="mb-1"
        >
          <b-form-group :label="$t('ledger_as_of')">
            <b-form-input
              v-model="asOf"
              type="date"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="canTrialBalance"
          cols="12"
          md="4"
          class="d-flex align-items-end mb-1"
        >
          <b-link :to="trialBalanceRoute">
            {{ $t('ledger_tb_title') }}
          </b-link>
        </b-col>
      </b-row>
    </b-card>

    <b-alert
      v-if="loaded && !loading"
      show
      :variant="report.matched ? 'success' : 'danger'"
    >
      <div class="d-flex justify-content-between align-items-center">
        <strong>{{ report.matched ? $t('ledger_recon_matched') : $t('ledger_recon_unmatched') }}</strong>
        <span
          v-if="!report.matched"
          class="ledger-num"
        >
          {{ money(report.totals && report.totals.difference) }}
        </span>
      </div>
    </b-alert>

    <b-alert
      v-if="hasUnmapped && !loading"
      show
      variant="warning"
    >
      <div class="font-weight-bold mb-50">
        {{ $t('ledger_recon_unmapped_warning') }}
      </div>
      <div
        v-if="unmappedAccounts.length"
        class="mb-50"
      >
        <div class="mb-25">
          {{ $t('ledger_recon_unmapped_accounts') }}
        </div>
        <div
          v-for="row in unmappedAccounts"
          :key="`ua-${row.id}`"
          class="ledger-num"
        >
          {{ row.code }} — {{ row.name }} · {{ money(row.current_balance) }}
        </div>
      </div>
      <div
        v-if="unmappedWallets.length"
        class="mb-50"
      >
        <div class="mb-25">
          {{ $t('ledger_recon_unmapped_wallets') }}
        </div>
        <div
          v-for="row in unmappedWallets"
          :key="`uw-${row.id}`"
          class="ledger-num"
        >
          {{ $t('ledger_employee') }} {{ row.employee_id }} · {{ money(row.current_balance) }}
        </div>
      </div>
      <b-button
        v-if="canOpenLinks"
        variant="warning"
        size="sm"
        class="rounded-pill mt-50"
        :to="{ name: 'ledger-links' }"
      >
        {{ $t('ledger_links_title') }}
      </b-button>
    </b-alert>

    <b-card class="rounded-lg">
      <div
        v-if="loading"
        class="ledger-skeleton"
        aria-hidden="true"
      >
        <div
          v-for="n in 5"
          :key="n"
          class="ledger-skeleton-row"
        />
      </div>

      <div
        v-else-if="!groups.length"
        class="text-center text-muted py-3"
      >
        {{ $t('ledger_recon_empty') }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="groups"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
            thead-class="ledger-sticky-head"
            foot-clone
          >
            <template #cell(label)="data">
              {{ data.item.label || data.item.name }}
            </template>
            <template #cell(code)="data">
              <strong>{{ data.item.code }}</strong>
            </template>
            <template #cell(subsidiary)="data">
              <span class="ledger-num">{{ money(data.item.subsidiary) }}</span>
            </template>
            <template #cell(ledger)="data">
              <span class="ledger-num">{{ money(data.item.ledger) }}</span>
            </template>
            <template #cell(difference)="data">
              <span
                class="ledger-num"
                :class="{ 'text-danger font-weight-bold': isNonZero(data.item.difference) }"
              >
                {{ money(data.item.difference) }}
              </span>
            </template>
            <template #foot(label)>
              {{ $t('ledger_difference') }}
            </template>
            <template #foot(code)>
              <span />
            </template>
            <template #foot(subsidiary)>
              <span />
            </template>
            <template #foot(ledger)>
              <span />
            </template>
            <template #foot(difference)>
              <strong
                class="ledger-num"
                :class="{ 'text-danger': isNonZero(report.totals && report.totals.difference) }"
              >
                {{ money(report.totals && report.totals.difference) }}
              </strong>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <div
            v-for="row in groups"
            :key="row.gl_account_id || row.code"
            class="ledger-recon-card"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ row.label || row.name }}</strong>
              <span class="ledger-num">{{ row.code }}</span>
            </div>
            <div class="d-flex justify-content-between ledger-num mt-50">
              <span>{{ $t('ledger_recon_subsidiary') }} {{ money(row.subsidiary) }}</span>
              <span>{{ $t('ledger_recon_ledger') }} {{ money(row.ledger) }}</span>
            </div>
            <div
              class="ledger-num mt-25"
              :class="{ 'text-danger font-weight-bold': isNonZero(row.difference) }"
            >
              {{ $t('ledger_difference') }} {{ money(row.difference) }}
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <div
      v-if="loaded && !loading"
      class="ledger-recon-bar d-md-none"
    >
      <strong>{{ report.matched ? $t('ledger_recon_matched') : $t('ledger_recon_unmatched') }}</strong>
      <span
        class="ledger-num"
        :class="{ 'text-danger': isNonZero(report.totals && report.totals.difference) }"
      >
        {{ money(report.totals && report.totals.difference) }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BTable, BAlert, BLink, BButton,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getLedgerReconciliation } from '@/api/accounting/ledgerReconciliation'
import {
  oneFrom, formatLedgerMoney, todayIso, apiErrorMessage,
} from '@/utils/ledger'

const emptyReport = () => ({
  as_of: '',
  matched: false,
  totals: { difference: null },
  groups: [],
  unmapped: { accounts: [], wallets: [] },
})

export default {
  name: 'LedgerReconciliation',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BTable, BAlert, BLink, BButton,
  },
  data() {
    return {
      loading: false,
      loaded: false,
      asOf: todayIso(),
      report: emptyReport(),
    }
  },
  computed: {
    groups() {
      return Array.isArray(this.report.groups) ? this.report.groups : []
    },
    unmappedAccounts() {
      const accounts = this.report.unmapped && this.report.unmapped.accounts
      return Array.isArray(accounts) ? accounts : []
    },
    unmappedWallets() {
      const wallets = this.report.unmapped && this.report.unmapped.wallets
      return Array.isArray(wallets) ? wallets : []
    },
    hasUnmapped() {
      return this.loaded && (this.unmappedAccounts.length > 0 || this.unmappedWallets.length > 0)
    },
    canOpenLinks() {
      return this.hasPermission('edit gl_links')
    },
    canTrialBalance() {
      return this.hasPermission('view trial_balance')
    },
    trialBalanceRoute() {
      return { name: 'ledger-trial-balance', query: { as_of: this.asOf } }
    },
    fields() {
      return [
        { key: 'label', label: this.$t('ledger_recon_statement') },
        { key: 'code', label: this.$t('ledger_recon_account_no') },
        { key: 'subsidiary', label: this.$t('ledger_recon_subsidiary') },
        { key: 'ledger', label: this.$t('ledger_recon_ledger') },
        { key: 'difference', label: this.$t('ledger_difference') },
      ]
    },
  },
  watch: {
    asOf: 'loadReport',
  },
  mounted() {
    this.loadReport()
  },
  methods: {
    money(value) {
      if (value == null || value === '') return '—'
      return formatLedgerMoney(value, this.$i18n.locale)
    },
    isNonZero(value) {
      const n = Number(value)
      return Number.isFinite(n) && Math.abs(n) >= 0.005
    },
    async loadReport() {
      if (!this.asOf) return
      this.loading = true
      try {
        const res = await getLedgerReconciliation({ as_of: this.asOf })
        const data = oneFrom(res) || {}
        this.report = {
          as_of: data.as_of || this.asOf,
          matched: data.matched === true,
          totals: data.totals || { difference: null },
          groups: Array.isArray(data.groups) ? data.groups : [],
          unmapped: data.unmapped || { accounts: [], wallets: [] },
        }
        this.loaded = true
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-recon-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
}
.ledger-recon-bar {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 2px solid #06284d;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}
.ledger-sticky-head th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.ledger-dense-table ::v-deep tfoot th {
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 1;
  border-top: 2px solid #06284d;
}
.ledger-skeleton-row {
  height: 2.25rem;
  margin-bottom: 0.5rem;
  border-radius: 6px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e6e9ee 37%, #f2f4f7 63%);
  background-size: 400% 100%;
  animation: ledger-shimmer 1.2s ease infinite;
}
@keyframes ledger-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
</style>
